/**
 * Voluum-style path settings: destination, transition, distribution AI + flow strip.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import type {
  VoluumOfferRedirectMode,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import PathSettingsDistribution from './PathSettingsDistribution';
import PathTransitionFlowStrip from './path-transition';
import PillSegmentRow from './PillSegmentRow';
import {
  PATH_DESTINATION_OPTIONS,
  type PathDestinationMode,
} from './path-settings.constants';
import {
  PathSettingsDivider,
  PathSettingsSectionTitle,
} from './path-settings-layout';
import { usePathDestinationTransition } from './path-transition/use-path-destination-transition';

export interface PathSettingsPanelProps {
  path: VoluumPathUpdate;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
  compact?: boolean;
}

const PathSettingsPanel: FC<PathSettingsPanelProps> = ({
  path,
  onPatch,
  compact = false,
}) => {
  const { destination, setDestination, transitionOptions, transitionValue } =
    usePathDestinationTransition(path, onPatch);

  const sectionGap = compact ? 'space-y-1.5' : 'space-y-2';

  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-muted/10',
        compact ? 'space-y-2 p-2' : 'space-y-4 p-3 sm:p-4',
      )}
    >
      <section className={sectionGap}>
        <PathSettingsSectionTitle dense={compact}>
          Path destination
        </PathSettingsSectionTitle>
        <PillSegmentRow<PathDestinationMode>
          aria-label="Path destination"
          value={destination}
          options={PATH_DESTINATION_OPTIONS}
          onChange={setDestination}
          compact={compact}
        />
        {destination === 'LANDERS_AND_OFFERS' && path.landers.length === 0 && (
          <p
            className={cn(
              'text-muted-foreground',
              compact ? 'text-[10px] leading-snug' : 'text-xs',
            )}
          >
            Add landers below — this path uses a lander step before offers.
          </p>
        )}
      </section>
      <PathSettingsDivider />
      <section className={sectionGap}>
        <PathSettingsSectionTitle
          dense={compact}
          hint={
            destination === 'OFFERS_ONLY'
              ? 'How visitors move from Voluum to the offer URL.'
              : 'How visitors move from the lander experience to the offer URL.'
          }
        >
          Transition to offer
        </PathSettingsSectionTitle>
        <PillSegmentRow<VoluumOfferRedirectMode>
          aria-label="Transition to offer"
          value={transitionValue}
          options={transitionOptions}
          onChange={(v) =>
            onPatch((p) => {
              p.offerRedirectMode = v;
            })
          }
          compact={compact}
        />
        <PathTransitionFlowStrip
          mode={transitionValue}
          destination={destination}
          compact={compact}
        />
      </section>
      <PathSettingsDivider />
      <PathSettingsDistribution
        path={path}
        onPatch={onPatch}
        compact={compact}
      />
      {/* Hidden: listicle path, PathSettingsMoreFields. */}
    </div>
  );
};

export default PathSettingsPanel;
